import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useCombobox } from 'downshift';
import { invert } from 'lodash';
import { createBemBlockBuilder, SearchAutocomplete } from '@ebsco-ui/ebsco-ui';

import {
  AutoCompleteGroups,
  FeatureFlagDisplay,
  SearchOption,
  SearchOptionType,
} from '@app/components';
import { useAutoComplete, useFeatureFlag } from '@app/hooks';
import { getFilteredValues } from '@app/utils';
import { sharedMessages } from '@app/translations';
import { Feature } from '@app/constants';

import {
  AdvancedSearchField,
  advancedSearchFields,
  advancedSearchOptions,
  AdvancedSearchQualifier,
  BooleanOperator,
  ConsortiaAdvancedSearchField,
  consortiaAdvancedSearchFields,
  consortiaFieldToQualifierMap,
  fieldsToSearchOptionMap,
  RowValues,
  searchOptionToFieldMap,
  typesMap,
} from '../constants';
import { FieldsDropdown } from '../FieldsDropdown';

import './SearchRow.scss';

interface SearchRowProps extends RowValues {
  index: number;
  totalRowsCount: number;
  updateRow: (data: RowValues) => void;
}

const cnBem = createBemBlockBuilder(['advancedSearchRow']);

const advancedOptionToSearchOptionMap = {
  [advancedSearchOptions.keyword]: SearchOption.keyword,
  [advancedSearchOptions.title]: SearchOption.title,
  [advancedSearchOptions.subjects]: SearchOption.subject,
  [advancedSearchOptions.contributors]: SearchOption.author,
};

export const mapSearchOptionToField = (option: SearchOptionType) =>
  searchOptionToFieldMap[
    invert(advancedOptionToSearchOptionMap)[option]
  ] as AdvancedSearchField;

export const SearchRow: FC<SearchRowProps> = ({
  value,
  field,
  qualifier,
  index,
  totalRowsCount,
  booleanOperator,
  updateRow,
}) => {
  const { $t } = useIntl();
  const { isActive: isLCAdvancedSearchOptionsActive } = useFeatureFlag({
    name: Feature.LCAdvancedSearchOptions,
  });
  const { isActive: isConsortiaLogin } = useFeatureFlag({
    name: Feature.consortiaLoginFlow,
  });

  const searchOption =
    advancedOptionToSearchOptionMap[fieldsToSearchOptionMap[field]];
  const isKeywordSearch = searchOption === SearchOption.keyword;
  const fieldTypeOptions = isConsortiaLogin
    ? consortiaAdvancedSearchFields
    : getFilteredValues(advancedSearchFields, [
        !isLCAdvancedSearchOptionsActive && AdvancedSearchField.titleMain,
        !isLCAdvancedSearchOptionsActive && AdvancedSearchField.namePersonal,
      ]);

  const getQualifierBasedOnField = (
    newField: AdvancedSearchField | ConsortiaAdvancedSearchField
  ) => (isConsortiaLogin ? consortiaFieldToQualifierMap[newField] : qualifier);

  const onSelectItem = ({ selectedValue, selectedOption }) => {
    updateRow({
      value: selectedValue,
      field: isConsortiaLogin
        ? selectedOption
        : mapSearchOptionToField(selectedOption),
      booleanOperator,
      qualifier: getQualifierBasedOnField(selectedOption),
    });
  };

  const { inputValue, suggestions, handleSelectItem, handleInputValueChange } =
    useAutoComplete({ value, searchOption, onSelectItem });

  const handleInputChange = changes => {
    handleInputValueChange(changes);

    if (
      changes.type === useCombobox.stateChangeTypes.InputChange ||
      changes.type === useCombobox.stateChangeTypes.FunctionReset
    ) {
      updateRow({
        value: changes.inputValue,
        booleanOperator,
        field,
        qualifier,
      });
    }
  };

  const handleBooleanChipChange = (updatedChip: BooleanOperator) => {
    updateRow({ value, field, qualifier, booleanOperator: updatedChip });
  };

  const handleFieldChange = (updatedField: AdvancedSearchField) => {
    updateRow({
      value,
      booleanOperator,
      field: updatedField,
      qualifier: getQualifierBasedOnField(updatedField),
    });
  };

  const handleQualifierChange = (updatedQualifier: AdvancedSearchQualifier) => {
    updateRow({
      value,
      booleanOperator,
      field,
      qualifier: updatedQualifier,
    });
  };

  const autocompleteLabel = $t({
    id: 'advancedSearch.inputField',
    defaultMessage: 'Search input field',
  });
  const legendId = `row-${index}-legend`;

  return (
    <fieldset
      role="group"
      aria-label={$t({
        id: 'advancedSearch.row',
        defaultMessage: 'Row',
      })}
      aria-describedby={legendId}
      className={cnBem()}
    >
      <legend id={legendId} className={cnBem('__legend')}>
        {$t(
          {
            id: 'advancedSearch.rowTitle',
            defaultMessage: 'Row {number} of {total}.',
          },
          {
            number: index + 1,
            total: totalRowsCount,
          }
        )}
      </legend>
      <div className={cnBem('__wrapper')}>
        <div className={cnBem('__autocompleteGroup')}>
          {booleanOperator && (
            <FieldsDropdown
              option={booleanOperator}
              typeOptions={typesMap.booleanOperator}
              id={`row-${index}-boolean-operator`}
              className={cnBem('__booleanOperatorDropdown')}
              onSelect={handleBooleanChipChange}
            />
          )}
          <SearchAutocomplete
            items={suggestions}
            {...(isKeywordSearch && {
              groups: AutoCompleteGroups,
              groupItemsBy: item => item.category,
            })}
            value={inputValue}
            messages={{
              autocompleteMessages: {
                input: {
                  'aria-label': autocompleteLabel,
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  'aria-describedby': legendId,
                },
                clearButton: {
                  'aria-label': $t(sharedMessages.clear),
                },
                menu: {
                  'aria-label': autocompleteLabel,
                },
              },
            }}
            displayHighlightedValue
            focusedByDefault={index === 0}
            className={cnBem('__autocomplete')}
            downshiftOptions={{
              menuId: `row-${index}-autocomplete-menu`,
              onSelectedItemChange: handleSelectItem,
            }}
            onChange={handleInputChange}
          />
        </div>
        <FeatureFlagDisplay
          flagToMatch={{ name: Feature.consortiaLoginFlow }}
          renderOff={() => (
            <FieldsDropdown
              option={qualifier}
              typeOptions={typesMap.qualifier}
              id={`row-${index}-qualifier`}
              className={cnBem('__dropdown')}
              onSelect={handleQualifierChange}
            />
          )}
        />
        <FieldsDropdown
          option={field}
          typeOptions={fieldTypeOptions}
          id={`row-${index}-field`}
          className={cnBem('__dropdown')}
          onSelect={handleFieldChange}
        />
      </div>
    </fieldset>
  );
};
